"use strict";
// Draws the reviews of one product, read from Flix Identity.
//
// The section has already printed the average, the star row and the count
// from Shopify's metafields, so this never renders an empty box first and
// fills it in: it replaces numbers only where the live ones differ, and
// appends the list under a summary that was already correct.
//
// Reviewer names and review bodies are other people's text. Everything
// this file puts on the page goes in through textContent; there is no
// innerHTML path for anything that came from the network.
(function () {
  var PAGE_SIZE = 10;

  function apiBase() {
    var config = window.flixIdentityConfig;
    if (!config || !config.apiBaseUrl) return null;
    return String(config.apiBaseUrl).replace(/\/+$/, "");
  }

  function el(tag, styles, text) {
    var node = document.createElement(tag);
    if (styles) node.setAttribute("style", styles);
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  // Stars as characters, matching what the section printed server-side.
  function starRow(rating) {
    var row = el("div", "flex:none;font-size:15px;letter-spacing:2px;color:var(--accent)");
    row.setAttribute("dir", "ltr");
    var stars = "";
    for (var i = 1; i <= 5; i++) stars += i <= rating ? "★" : "☆";
    row.textContent = stars;
    return row;
  }

  function formatDate(iso) {
    if (!iso) return "";
    var date = new Date(iso);
    if (isNaN(date.getTime())) return "";
    try {
      return date.toLocaleDateString(document.documentElement.lang || "ar", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return date.toISOString().slice(0, 10);
    }
  }

  function reviewCard(review, labels) {
    var card = el(
      "article",
      "padding:18px 20px;border-radius:16px;background:var(--surface);border:1px solid var(--border)"
    );

    var head = el("div", "display:flex;align-items:flex-start;gap:12px");

    // Who wrote it, with the date as a caption under the name.
    var who = el("div", "min-width:0;display:flex;flex-direction:column;gap:3px");

    var nameLine = el("div", "display:flex;align-items:center;gap:8px;flex-wrap:wrap");
    nameLine.appendChild(
      el(
        "span",
        "font-family:var(--display);font-weight:800;font-size:15px;color:var(--text)",
        review.authorName
      )
    );
    if (review.isVerifiedPurchase && labels.verified) {
      nameLine.appendChild(
        el(
          "span",
          "padding:3px 9px;border-radius:99px;background:var(--bg2);border:1px solid var(--borderHi);font-size:11.5px;font-weight:700;color:var(--accent)",
          labels.verified
        )
      );
    }
    who.appendChild(nameLine);

    var date = formatDate(review.publishedAt);
    if (date) {
      who.appendChild(el("span", "font-size:12px;color:var(--dim)", date));
    }
    head.appendChild(who);

    // The stars go to the far end of the row — the left, in a right-to-left
    // layout — rather than beside the name.
    var ratingCell = starRow(review.rating);
    ratingCell.style.marginInlineStart = "auto";
    head.appendChild(ratingCell);

    card.appendChild(head);

    if (review.title) {
      card.appendChild(
        el(
          "h3",
          "margin:12px 0 0;font-family:var(--display);font-weight:800;font-size:16px;color:var(--text);line-height:1.5",
          review.title
        )
      );
    }

    card.appendChild(
      el(
        "p",
        "margin:10px 0 0;font-size:15px;line-height:1.85;color:var(--dim);white-space:pre-wrap;text-wrap:pretty",
        review.body
      )
    );

    // The store's answer sits inside the review it answers, indented, so
    // it reads as a reply rather than as another review.
    if (review.reply) {
      var reply = el(
        "div",
        "margin:14px 0 0;padding:13px 16px;border-radius:12px;background:var(--bg2);border-inline-start:3px solid var(--accent)"
      );
      if (labels.reply) {
        reply.appendChild(
          el("div", "font-family:var(--display);font-weight:800;font-size:13px;color:var(--accent)", labels.reply)
        );
      }
      reply.appendChild(
        el("p", "margin:6px 0 0;font-size:14px;line-height:1.8;color:var(--dim);white-space:pre-wrap", review.reply)
      );
      card.appendChild(reply);
    }

    return card;
  }

  function paintSummary(root, summary, labels) {
    if (!summary || summary.total === 0) return;

    var average = root.querySelector("[data-flix-reviews-average]");
    if (average) average.textContent = summary.average.toFixed(1);

    var stars = root.querySelector("[data-flix-reviews-stars]");
    if (stars) {
      var filled = Math.round(summary.average);
      var text = "";
      for (var i = 1; i <= 5; i++) text += i <= filled ? "★" : "☆";
      stars.textContent = text;
    }

    var count = root.querySelector("[data-flix-reviews-count]");
    if (count) count.textContent = summary.total + " " + labels.count;

    // distribution is counts per star, index 0 being one star.
    for (var star = 1; star <= 5; star++) {
      var value = summary.distribution[star - 1] || 0;
      var bar = root.querySelector('[data-flix-reviews-bar="' + star + '"]');
      var barCount = root.querySelector('[data-flix-reviews-bar-count="' + star + '"]');
      if (bar) bar.style.width = Math.round((value / summary.total) * 100) + "%";
      if (barCount) barCount.textContent = String(value);
    }
  }

  async function load(root, state) {
    var base = apiBase();
    if (!base) return;

    var url =
      base +
      "/reviews?productId=" +
      encodeURIComponent(root.dataset.productGid) +
      "&limit=" +
      PAGE_SIZE +
      "&offset=" +
      state.offset;

    var response;
    try {
      response = await fetch(url, { headers: { Accept: "application/json" } });
    } catch (e) {
      // Offline, or the service is down. The section keeps the summary it
      // printed server-side, which is still true — a failed fetch is no
      // reason to replace correct numbers with an error.
      return;
    }
    if (!response.ok) return;

    var payload;
    try {
      payload = await response.json();
    } catch (e) {
      return;
    }
    if (!payload || !Array.isArray(payload.reviews)) return;

    var labels = state.labels;
    paintSummary(root, payload.summary, labels);

    var list = root.querySelector("[data-flix-reviews-list]");
    if (!list) return;

    if (payload.reviews.length > 0) {
      var empty = list.querySelector("[data-flix-reviews-empty]");
      if (empty) empty.remove();
    }

    payload.reviews.forEach(function (review) {
      list.appendChild(reviewCard(review, labels));
    });

    state.offset += payload.reviews.length;

    var more = root.querySelector("[data-flix-reviews-more]");
    if (more) more.hidden = state.offset >= payload.total;
  }

  // "I bought this — send me the link." The response is the same whether
  // or not a purchase was found, so the message shown is too.
  function wireRequestForm(root) {
    var box = root.querySelector("[data-flix-review-request]");
    if (!box) return;
    var form = box.querySelector("[data-flix-review-request-form]");
    var done = box.querySelector("[data-flix-review-request-done]");
    if (!form) return;

    form.addEventListener("submit", async function (event) {
      event.preventDefault();
      var base = apiBase();
      if (!base) return;

      var button = form.querySelector("button");
      var input = form.querySelector("input[name=email]");
      if (!input || !input.value.trim()) return;
      if (button) button.disabled = true;

      try {
        await fetch(base + "/reviews/request-link", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            email: input.value.trim(),
            productId: root.dataset.productGid,
            productHandle: root.dataset.productHandle || ""
          })
        });
      } catch (e) {
        // Even a network failure shows the same message: the page must
        // not become a way to tell one outcome from another.
      }

      form.hidden = true;
      if (done) done.hidden = false;
    });
  }
  function init(root) {
    if (root.dataset.flixReviewsReady === "1") return;
    root.dataset.flixReviewsReady = "1";

    var state = {
      offset: 0,
      labels: {
        verified: root.dataset.verifiedLabel || "",
        reply: root.dataset.replyLabel || "",
        count: root.dataset.countLabel || "",
      },
    };

    var more = root.querySelector("[data-flix-reviews-more]");
    if (more) {
      more.addEventListener("click", function () {
        more.disabled = true;
        load(root, state).finally(function () {
          more.disabled = false;
        });
      });
    }

    wireRequestForm(root);
    load(root, state);
  }

  function boot() {
    document.querySelectorAll("[data-flix-reviews]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // The theme editor re-renders one section at a time; without this a
  // merchant changing a setting sees the frame come back empty.
  document.addEventListener("shopify:section:load", function (event) {
    event.target.querySelectorAll("[data-flix-reviews]").forEach(init);
  });
})();
